import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PRODUCTS } from "../mock/mock";
import { useProducts } from "../context/ProductsContext";
import { whatsappLink, BRAND } from "../mock/mock";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import ProductCard from "../components/ProductCard";
import { WhatsAppIcon } from "../components/WhatsAppFloat";
import { Award, BadgeCheck, Heart, Home, Leaf, Minus, Package, Plus, ShieldCheck, ShoppingBag, Star, Truck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const ProductPage = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { products } = useProducts();
  const product = products.find((p) => p.id === id) || PRODUCTS.find((p) => p.id === id);
  const [qty, setQty] = useState(1);
  const { addItem, toggleWish, inWishlist } = useCart();
  const { toast } = useToast();

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto p-14 text-center">
        <h1 className="font-serif-brand text-3xl">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-brand font-semibold">Back to shop</Link>
      </div>
    );
  }

  const related = products.filter((p) => p.id !== product.id && (p.category === product.category)).slice(0, 4);

  const addToCart = () => {
    addItem(product, qty);
    toast({ title: "Added to cart", description: `${product.name} · Qty ${qty}` });
  };

  const buyNow = () => {
    addItem(product, qty);
    nav("/cart");
  };

  return (
    <main className="bg-cream">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 pt-6">
        <div className="flex items-center gap-2 text-[12.5px] text-[#6b4f39]">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-brand"><Home className="h-3.5 w-3.5" /> Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-brand">Shop</Link>
          <span>/</span>
          <span className="text-brand font-semibold line-clamp-1">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#f9f1de] border border-[#efe4c9]">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
            {product.discount > 0 && (
              <span className="absolute top-4 left-4 bg-brand text-white text-[12px] font-bold px-3 py-1.5 rounded-md">{product.discount}% OFF</span>
            )}
          </div>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {[product.image, product.image, product.image, product.image].map((im, i) => (
              <div key={i} className={`aspect-square rounded-xl overflow-hidden border ${i === 0 ? "border-brand" : "border-[#efe4c9]"} bg-white`}>
                <img src={im} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <div className="text-[11px] tracking-[0.28em] font-bold text-olive">AGRAWAL RICH DRY FRUITS</div>
          <h1 className="font-serif-brand text-[30px] md:text-[38px] font-bold text-[#2a1a15] leading-tight mt-1.5">{product.name}</h1>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex items-center gap-1 text-gold">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? "fill-current" : ""}`} />)}</div>
            <span className="text-[13px] text-[#6b4f39]">{product.rating} · 240+ ratings</span>
          </div>

          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-serif-brand text-[36px] font-bold text-[#2a1a15]">₹{product.price.toLocaleString("en-IN")}</span>
            {product.mrp > product.price && <span className="text-lg line-through text-[#a08774]">₹{product.mrp.toLocaleString("en-IN")}</span>}
            {product.discount > 0 && <span className="text-olive font-semibold text-sm">Save ₹{(product.mrp - product.price).toLocaleString("en-IN")}</span>}
          </div>
          <div className="text-[12.5px] text-[#6b4f39] mt-1">Inclusive of all taxes · Weight: <b className="text-[#2a1a15]">{product.weight}</b></div>

          <p className="mt-5 text-[15px] text-[#3a2a20] leading-relaxed">{product.description}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {["NATURAL", "NO PRESERVATIVES", "HYGIENIC PACKING"].map((t) => (
              <span key={t} className="text-[11px] font-bold tracking-wider text-olive bg-[#eef2df] px-2.5 py-1 rounded-md">{t}</span>
            ))}
          </div>

          {/* Qty + CTA */}
          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center bg-white rounded-full border border-[#e3d3ad] overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center hover:bg-[#f9f1de]"><Minus className="h-4 w-4" /></button>
              <div className="w-10 text-center font-semibold">{qty}</div>
              <button onClick={() => setQty(qty + 1)} className="h-11 w-11 flex items-center justify-center hover:bg-[#f9f1de]"><Plus className="h-4 w-4" /></button>
            </div>
            <button onClick={addToCart} className="flex-1 h-12 rounded-full bg-brand hover:bg-[#6c1414] text-white font-semibold flex items-center justify-center gap-2 transition-colors">
              <ShoppingBag className="h-4 w-4" /> Add to Cart
            </button>
            <button onClick={() => toggleWish(product)} aria-label="Wishlist" className="h-12 w-12 rounded-full bg-white border border-[#e3d3ad] flex items-center justify-center hover:border-brand hover:text-brand transition-colors">
              <Heart className={`h-5 w-5 ${inWishlist(product.id) ? "fill-brand text-brand" : ""}`} />
            </button>
          </div>
          <button onClick={buyNow} className="mt-3 w-full h-12 rounded-full bg-gold hover:bg-[#b6944f] text-[#2a1a15] font-semibold transition-colors">Buy Now</button>
          <a
            href={whatsappLink(`Hello Agrawal Rich Dry Fruits, I'd like to order:\n\n${product.name} \u2014 ${product.weight} \u00d7 ${qty}\nPrice: \u20b9${(product.price * qty).toLocaleString('en-IN')}\n\nPlease confirm availability. Thank you!`)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold transition-colors"
          >
            <WhatsAppIcon className="h-5 w-5" /> Order on WhatsApp
          </a>

          {/* Trust */}
          <div className="mt-6 grid grid-cols-2 gap-3">
            {[
              { i: Truck, t: "Free shipping over ₹1499" },
              { i: ShieldCheck, t: "100% quality assurance" },
              { i: Package, t: "Hygienically packed" },
              { i: Award, t: "Trusted since 1973" },
            ].map((x, i) => (
              <div key={i} className="flex items-center gap-2.5 bg-white border border-[#efe4c9] rounded-xl p-3">
                <x.i className="h-5 w-5 text-brand" />
                <span className="text-[13px] text-[#3a2a20]">{x.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-4">
        <Tabs defaultValue="desc" className="w-full">
          <TabsList className="bg-transparent border-b border-[#efe4c9] rounded-none justify-start w-full h-auto p-0 gap-6">
            <TabsTrigger value="desc" className="data-[state=active]:text-brand data-[state=active]:border-brand border-b-2 border-transparent rounded-none px-1 py-3 font-serif-brand text-base">Description</TabsTrigger>
            <TabsTrigger value="nutri" className="data-[state=active]:text-brand data-[state=active]:border-brand border-b-2 border-transparent rounded-none px-1 py-3 font-serif-brand text-base">Nutrition</TabsTrigger>
            <TabsTrigger value="ship" className="data-[state=active]:text-brand data-[state=active]:border-brand border-b-2 border-transparent rounded-none px-1 py-3 font-serif-brand text-base">Shipping</TabsTrigger>
          </TabsList>
          <TabsContent value="desc" className="py-6 text-[15px] text-[#3a2a20] leading-relaxed max-w-3xl">
            {product.description} Sourced with care, our nuts and dry fruits go through multiple quality checks and are packed in hygienic facilities to lock in freshness, aroma, and nutrition. Perfect for daily snacking, cooking, festive gifting and everyday nourishment.
          </TabsContent>
          <TabsContent value="nutri" className="py-6 text-[15px] text-[#3a2a20]">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {[
                { l: "Energy", v: "575 kcal" }, { l: "Protein", v: "21 g" }, { l: "Fat", v: "49 g" }, { l: "Fibre", v: "12 g" },
              ].map((n) => (
                <div key={n.l} className="bg-white border border-[#efe4c9] rounded-xl p-4 text-center">
                  <div className="font-serif-brand text-xl font-bold text-brand">{n.v}</div>
                  <div className="text-[12px] text-[#6b4f39]">{n.l} · per 100g</div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="ship" className="py-6 text-[15px] text-[#3a2a20] leading-relaxed max-w-3xl">
            Free shipping across India on orders above ₹1499. Standard delivery: 3–6 business days. COD available on select pin codes. Easy 7-day return on unopened packs.
          </TabsContent>
        </Tabs>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 mt-10">
          <h2 className="font-serif-brand text-[26px] md:text-[32px] font-bold text-[#2a1a15] mb-5">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductPage;
